import React, {ReactNode} from 'react';
import {Form, Formik} from 'formik';
import {useDispatch} from 'react-redux';

import {saveCollegeCourse} from 'shared/portfolio/actions/college-courses';
import {Grade, Institution} from 'src/types/store';

export interface CollegeCourse {
  _id: string;
  courseCode: string;
  creditHours: number;
  grade: Grade;
  institution: Institution;
  name: string;
  semester: string;
  subject: string;
  year: number;
  courseListingId?: number;
  level?: number;

  // not from the API, FE only
  editing?: boolean;
  new?: boolean;
  scrollTo?: boolean;
}

export type CollegeCourseForm = Omit<CollegeCourse, 'institution' | 'creditHours' | 'year'> & {
  institution: Pick<Institution, 'name' | 'parentId' | 'type' | '_id'>;
  creditHours: number | null;
  year: number | '';
};

interface Props {
  initialValues: CollegeCourseForm;
  children: ReactNode;
  onSuccess?: () => void;
}

const CollegeCourseFormWrapper = ({initialValues, children, onSuccess}: Props) => {
  const dispatch = useDispatch();

  return (
    <Formik
      initialValues={initialValues}
      validate={(values) => {
        const errors: Partial<Record<keyof CollegeCourseForm, string>> = {};
        const currentYear = new Date().getFullYear();

        if (!values.name) {
          errors.name = 'Course name is required.';
        }

        if (!values.institution.parentId) {
          errors.institution = 'Institution is required.';
        }

        if (!values.courseCode) {
          errors.courseCode = 'Course code is required.';
        }

        if (!values.subject) {
          errors.subject = 'Subject is required.';
        }

        if (values.creditHours === undefined) {
          errors.creditHours = 'Credit hours are required.';
        }

        if (!values.year) {
          errors.year = 'Year is required.';
        } else if (values.year < currentYear - 100 || values.year > currentYear) {
          errors.year = 'Year must be in the past 100 years.';
        }

        if (!values.semester) {
          errors.semester = 'Semester is required.';
        }

        return errors;
      }}
      onSubmit={async (values, formik) => {
        try {
          await dispatch(saveCollegeCourse(values));
          onSuccess?.();
        } catch (errors: $TSFixMe) {
          Object.keys(errors).forEach((field) => {
            if (errors[field]) {
              formik.setFieldError(field, errors[field]);
            }
          });
        }
      }}
    >
      <Form autoComplete="off">{children}</Form>
    </Formik>
  );
};

export default CollegeCourseFormWrapper;
